import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Autocomplete,
  Card,
  CardContent,
} from "@mui/material";
import {
  ArrowForward,
  AccountTree,
  Search,
  Refresh,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  AUTO_FILL_COMPANY_EMAIL,
  AUTO_FILL_PROGRAM_ENTRY_AMOUNT,
  getLevelAmount,
} from "../config/autoFillProgramConfig";
import { getAutoFillCoPointsWallet } from "../utils/autoFillCoPointsWallet";
import {
  aggregateAutoFillMetrics,
  buildMatrixPlanReferenceRows,
} from "../services/autoFill/autoFillAdminMetrics";
import {
  countFilledAtLevelFromPositions,
  pickNextEligibleMatrixRecipientSync,
} from "../services/autoFill/autoFillMatrixEligibility";
import { parseEnrollmentSeqFromProgramId } from "../config/autoFillProgramConfig";
import AutoFillLevel6ForfeitureAdmin from "./AutoFillLevel6ForfeitureAdmin";
import { sortProgramsForDisplay } from "../services/autoFill/autoFillProgramSpawn";
import {
  MAHESH_AUTO_FILL_PROGRAM_ID,
  MAHESH_L2_SERIAL_PAYMENTS,
  maheshL2SerialRouteForPaymentIndex,
  maheshPayIndexForFillerProgramId,
} from "../config/autoFillMaheshL2SerialConfig";

const formatInr = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function explainLedgerRow(row, userMap) {
  const filler = row.fillerUserId
    ? userMap[row.fillerUserId]?.email || row.fillerUserId
    : "—";
  const beneficiary = userMap[row.userId]?.email || row.userId;
  const amt = row.amount;

  switch (row.transactionType) {
    case "PROGRAM_CREATED":
      return {
        from: beneficiary,
        to: `Program ${row.programId}`,
        why: `New Auto Fill program started (package payment ₹${amt}).`,
        type: "join",
      };
    case "CO_POINTS":
      if (row.fromWallet === "UPGRADE") {
        return {
          from: filler !== "—" ? filler : beneficiary,
          to: `${AUTO_FILL_COMPANY_EMAIL} (admin)`,
          why: `Level activation payout — ₹${amt} from upgrade pool → Admin CO Points.`,
          type: "co",
        };
      }
      return {
        from: row.fromWallet === "EXTERNAL" ? beneficiary : filler,
        to: `${AUTO_FILL_COMPANY_EMAIL} (admin)`,
        why:
          row.fromWallet === "EXTERNAL"
            ? `Genesis AFP entry ₹${amt} → Admin CO Points.`
            : `CO Points ₹${amt} → admin. Program owner: ${beneficiary}.`,
        type: "co",
      };
    case "POINTS": {
      const payIdx = row.metadata?.matrixPaymentIndex || 1;
      const fillerProgramId =
        row.metadata?.fillerProgramId ||
        row.metadata?.sourceProgramId ||
        null;
      const fillerLabel = filler !== "—" ? filler : fillerProgramId || "—";
      if (row.metadata?.maheshL2SerialFlow) {
        const alloc = row.metadata?.allocation === "WITHDRAWAL" ? "Withdrawal (CO Points)" : "Upgrade pool";
        return {
          from: `Mahesh L2 pay #${payIdx} · filler ${fillerProgramId || "—"}`,
          to: `${beneficiary} · ${row.programId} L${row.level}`,
          why: `Mahesh L2 serial — payment ${payIdx} of 3 → ₹${amt} ${alloc} (${row.programId}, matrix L${row.level}).`,
          type: "points",
        };
      }
      const activation =
        row.metadata?.fillSource === "LEVEL_ACTIVATION_MATRIX" ||
        row.metadata?.kind === "LEVEL_ACTIVATION_MATRIX" ||
        String(row.reference || "").includes("LEVEL-ACTIVATION");
      return {
        from: fillerLabel,
        to: beneficiary,
        why: activation
          ? `Level activation — matrix L${row.level} payment ${payIdx} of 3 → CO Points (₹${amt}) · filler ${fillerProgramId || "—"}.`
          : payIdx === 1
            ? `Matrix L${row.level} — payment ${payIdx} of 3 → CO Points withdrawal (₹${amt}).`
            : `Matrix L${row.level} — payment ${payIdx} of 3 → CO Points (₹${amt}).`,
        type: "points",
      };
    }
    case "LEVEL_UPGRADE": {
      const payIdx = row.metadata?.matrixPaymentIndex;
      const unit = amt;
      if (row.metadata?.maheshL2SerialFlow) {
        return {
          from: `Mahesh L2 pay #${payIdx || "—"} · filler ${row.metadata?.fillerProgramId || "—"}`,
          to: `${beneficiary} · ${row.programId} L${row.level}`,
          why: `Mahesh L2 serial — payment ${payIdx} of 3 → ₹${unit} Next level upgrade (${row.programId}, matrix L${row.level}).`,
          type: "upgrade",
        };
      }
      if (payIdx === 2) {
        return {
          from: filler,
          to: beneficiary,
          why: `Matrix L${row.level} — payment 2 of 3 → reserved ₹${unit} (waits for payment 3).`,
          type: "upgrade",
        };
      }
      if (payIdx === 3) {
        return {
          from: filler,
          to: beneficiary,
          why: `Matrix L${row.level} — payment 3 of 3 → ₹${unit} + reserved ₹${unit} = ₹${unit * 2} unlocks next level.`,
          type: "upgrade",
        };
      }
      return {
        from: filler,
        to: beneficiary,
        why: `Upgrade pool +₹${amt} (legacy or non-matrix entry).`,
        type: "upgrade",
      };
    }
    case "UPGRADE_RESERVE": {
      const payIdx = row.metadata?.matrixPaymentIndex || 2;
      const unit = amt;
      if (payIdx === 3) {
        return {
          from: filler,
          to: beneficiary,
          why: `Matrix L${row.level} — payment 3 of 3 → ₹${unit} + reserved ₹${unit} = ₹${unit * 2} unlocks next level.`,
          type: "upgrade",
        };
      }
      return {
        from: filler,
        to: beneficiary,
        why: `Matrix L${row.level} — payment 2 of 3 → reserved ₹${unit} (not CO Points wallet).`,
        type: "upgrade",
      };
    }
    case "UPGRADE_RESERVE": {
      const payIdx = row.metadata?.matrixPaymentIndex || 2;
      const unit = amt;
      if (payIdx === 3) {
        return {
          from: filler,
          to: beneficiary,
          why: `Matrix L${row.level} — payment 3 of 3 → ₹${unit} + reserved ₹${unit} = ₹${unit * 2} unlocks next level.`,
          type: "upgrade",
        };
      }
      return {
        from: filler,
        to: beneficiary,
        why: `Matrix L${row.level} — payment 2 of 3 → reserved ₹${unit} (not CO Points wallet).`,
        type: "upgrade",
      };
    }
    case "LEVEL_ACTIVATED":
      return {
        from: "Upgrade pool",
        to: beneficiary,
        why: `Level ${row.level} activated using ₹${amt} from upgrade reserve.`,
        type: "level",
      };
    default:
      return {
        from: row.fromWallet || "—",
        to: row.toWallet || beneficiary,
        why: row.transactionType || "Transaction",
        type: "other",
      };
  }
}

const FlowArrow = ({ from, to, amount, why, type }) => {
  const color =
    type === "co"
      ? "#6a1b9a"
      : type === "points"
        ? "#2e7d32"
        : type === "upgrade"
          ? "#ed6c02"
          : type === "join"
            ? primaryBlue[700]
            : primaryBlue[600];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderLeft: `4px solid ${color}`,
        bgcolor: "rgba(255,255,255,0.9)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Chip size="small" label={from} sx={{ maxWidth: 220 }} />
        <ArrowForward sx={{ color }} />
        <Chip size="small" color="primary" label={to} sx={{ maxWidth: 220 }} />
        {amount != null && (
          <Chip size="small" label={`₹${amount}`} sx={{ fontWeight: 700 }} />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {why}
      </Typography>
    </Paper>
  );
};

const AutoFillAdminFlowDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [positions, setPositions] = useState([]);
  const [globalLedger, setGlobalLedger] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [allMatrixPositions, setAllMatrixPositions] = useState([]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      if (u.uid) m[u.uid] = u;
    });
    return m;
  }, [users]);

  const loadBase = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [userSnap, progSnap, ledgerSnap, withdrawalSnap, companySnap, posSnap] =
        await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "autoFillPrograms")),
          getDocs(query(collection(db, "autoFillLedger"), limit(2000))),
          getDocs(collection(db, "autoFillWithdrawals")),
          getDocs(
            query(
              collection(db, "users"),
              where("email", "==", AUTO_FILL_COMPANY_EMAIL),
              limit(1)
            )
          ),
          getDocs(collection(db, "autoFillMatrixPositions")),
        ]);

      setAllMatrixPositions(posSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      setUsers(
        userSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
      setPrograms(
        sortProgramsForDisplay(
          progSnap.docs.map((d) => ({ docId: d.id, ...d.data() }))
        )
      );
      const globalRows = ledgerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      globalRows.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
        const tb = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      setGlobalLedger(globalRows);
      setWithdrawals(
        withdrawalSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
      setCompanyProfile(
        companySnap.empty
          ? null
          : { id: companySnap.docs[0].id, ...companySnap.docs[0].data() }
      );
    } catch (e) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  const afpPrograms150 = useMemo(
    () =>
      programs.filter(
        (p) => Number(p.packageAmount) === AUTO_FILL_PROGRAM_ENTRY_AMOUNT
      ),
    [programs]
  );

  const filteredPrograms = useMemo(() => {
    const base = afpPrograms150;
    let list = base;
    if (searchEmail.trim()) {
      const q = searchEmail.trim().toLowerCase();
      list = base.filter(
        (p) =>
          (p.userEmail || "").toLowerCase().includes(q) ||
          (p.programId || "").toLowerCase().includes(q)
      );
    }
    return sortProgramsForDisplay(list);
  }, [afpPrograms150, searchEmail]);

  const selectedProgram = useMemo(
    () => programs.find((p) => p.programId === selectedProgramId),
    [programs, selectedProgramId]
  );

  useEffect(() => {
    if (!selectedProgram) {
      setLedger([]);
      setPositions([]);
      return;
    }

    (async () => {
      try {
        const [lSnap, pSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, "autoFillLedger"),
              where("programId", "==", selectedProgram.programId)
            )
          ),
          getDocs(
            query(
              collection(db, "autoFillMatrixPositions"),
              where("programDocId", "==", selectedProgram.docId || selectedProgram.id)
            )
          ),
        ]);
        const rows = lSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || 0;
          const tb = b.createdAt?.toMillis?.() || 0;
          return tb - ta;
        });
        setLedger(rows);
        setPositions(
          pSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              if (a.level !== b.level) return a.level - b.level;
              const aFilled = a.positionStatus === "filled";
              const bFilled = b.positionStatus === "filled";
              if (aFilled && bFilled) {
                const ta = new Date(a.filledAt || 0).getTime();
                const tb = new Date(b.filledAt || 0).getTime();
                if (ta !== tb) return ta - tb;
              }
              if (aFilled !== bFilled) return aFilled ? -1 : 1;
              return (a.positionIndex || 0) - (b.positionIndex || 0);
            })
        );
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [selectedProgram]);

  useEffect(() => {
    if (filteredPrograms.length && !selectedProgramId) {
      const genesis = filteredPrograms.find(
        (p) => p.programId === "AFP-000001" && !p.parentProgramId
      );
      setSelectedProgramId(genesis?.programId || filteredPrograms[0].programId);
    }
  }, [filteredPrograms, selectedProgramId]);

  const flowRows = useMemo(() => {
    const source = selectedProgram ? ledger : globalLedger.slice(0, 50);
    return source.map((row) => ({
      row,
      flow: explainLedgerRow(row, userMap),
    }));
  }, [ledger, globalLedger, selectedProgram, userMap]);

  const owner = selectedProgram
    ? userMap[selectedProgram.userId]
    : null;

  const planRows = useMemo(() => {
    const base = buildMatrixPlanReferenceRows();
    const rosterPrograms = programs.filter(
      (p) => p.spawnedFromWithdrawal !== true && !p.parentProgramId
    );
    const programsByEnrollmentSeq = new Map();
    rosterPrograms.forEach((p) => {
      const seq =
        p.enrollmentSeq ?? parseEnrollmentSeqFromProgramId(p.programId);
      if (seq != null) {
        programsByEnrollmentSeq.set(Number(seq), { ...p, id: p.docId });
      }
    });
    const filledCountAtLevel = new Map();
    rosterPrograms.forEach((p) => {
      for (let level = 1; level <= 15; level += 1) {
        filledCountAtLevel.set(
          `${p.docId}:${level}`,
          countFilledAtLevelFromPositions(allMatrixPositions, p.docId, level)
        );
      }
    });
    const excludeIds = selectedProgram ? [selectedProgram.programId] : [];
    return base.map((row) => {
      const pick = pickNextEligibleMatrixRecipientSync({
        programsByEnrollmentSeq,
        filledCountAtLevel,
        targetLevel: row.level,
        excludeProgramIds: excludeIds,
      });
      return {
        ...row,
        nextMatrixCoRecipient: pick
          ? `${pick.program.userEmail || pick.program.programId} (${pick.program.programId})`
          : "—",
      };
    });
  }, [programs, allMatrixPositions, selectedProgram]);

  const platformMetrics = useMemo(
    () =>
      aggregateAutoFillMetrics({
        ledgerRows: globalLedger,
        users,
        withdrawals,
        companyUser: companyProfile,
        programs: afpPrograms150,
      }),
    [globalLedger, users, withdrawals, companyProfile, afpPrograms150]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <AccountTree color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, color: primaryBlue[800] }}>
          Auto Fill — Money flow map
        </Typography>
        <Button startIcon={<Refresh />} size="small" onClick={loadBase}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <AutoFillLevel6ForfeitureAdmin embedded />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
        Platform totals (all programs)
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{platformMetrics.programCount}</strong> active AFP records ·{" "}
          <strong>{platformMetrics.programsAt150}</strong> Auto Fill programs at ₹
          {AUTO_FILL_PROGRAM_ENTRY_AMOUNT}. Entry total:{" "}
          <strong>{formatInr(platformMetrics.enrollmentVolume)}</strong>. (₹699 Ultra-only
          records are excluded
          {platformMetrics.programsExcludedNon150 > 0
            ? ` — ${platformMetrics.programsExcludedNon150} hidden`
            : ""}
          .) Matrix fills are separate from ₹150 entry.
        </Typography>
      </Alert>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", borderTop: `4px solid ${primaryBlue[800]}` }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                AFP package entries
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatInr(platformMetrics.enrollmentVolume)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sum of ₹150 Auto Fill program entries only. Ledger
                PROGRAM_CREATED: {formatInr(platformMetrics.ledgerProgramCreated)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", borderTop: "4px solid #6a1b9a" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                CO Points — admin account
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {platformMetrics.companyEmail}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatInr(platformMetrics.companyCoPointsEarned)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Operator CO ledger (fill #1). Available:{" "}
                {formatInr(platformMetrics.companyCoPointsAvailable)} · Ledger CO_POINTS:{" "}
                {formatInr(platformMetrics.ledgerCoPointsTotal)}
              </Typography>
              {platformMetrics.legacyMemberPointsFromOldEngine > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Includes {formatInr(platformMetrics.legacyMemberPointsFromOldEngine)} legacy
                  POINTS ledger rows.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", borderTop: "4px solid #ed6c02" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                On hold for upgrade
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatInr(platformMetrics.upgradeOnHold)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Current reserve only (not spent on level-up). Applied to levels:{" "}
                {formatInr(platformMetrics.upgradeUsedTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", borderTop: "4px solid #00838f" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Member points wallets (CO cash-out)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatInr(platformMetrics.memberCoPointsWalletAvailable)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Available to withdraw · {platformMetrics.membersWithPointsBalance} member(s)
                with balance
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Earned: {formatInr(platformMetrics.memberPointsEarnedTotal)} · Locked:{" "}
                {formatInr(platformMetrics.memberPointsLockedTotal)} · Withdrawn:{" "}
                {formatInr(platformMetrics.memberPointsWithdrawnTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", borderTop: "4px solid #2e7d32" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Withdrawals (requests)
              </Typography>
              <Typography variant="body2">
                Completed:{" "}
                <strong>{formatInr(platformMetrics.withdrawalCompleted)}</strong>
              </Typography>
              <Typography variant="body2">
                Pending:{" "}
                <strong>{formatInr(platformMetrics.withdrawalPending)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Member points wallet (legacy):{" "}
                {formatInr(platformMetrics.memberPointsWalletAvailable)} available
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Auto Fill matrix plan (reference — one full 15-level cycle)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Per level: 3 fills × Auto Fill; fill #1 → Points Wallet; fills #2–#3 →
          Upgrade pool (2× level amount to activate next level). Genesis AFP #1
          company entry → Admin CO Points only.
          Full-cycle CO cumulative:{" "}
          {formatInr(platformMetrics.planGrandTotalCo)}.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell align="right">Auto Fill</TableCell>
                <TableCell>Next CO recipient (matrix)</TableCell>
                <TableCell align="right">Fill Count</TableCell>
                <TableCell align="right">TOTAL</TableCell>
                <TableCell align="right">Auto Upgrade</TableCell>
                <TableCell align="right">CO Points</TableCell>
                <TableCell align="right">Cumulative CO</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planRows.map((r) => (
                <TableRow
                  key={r.level}
                  selected={
                    selectedProgram != null &&
                    Number(selectedProgram.currentLevel) === r.level
                  }
                >
                  <TableCell>{r.level}</TableCell>
                  <TableCell align="right">{formatInr(r.autoFill)}</TableCell>
                  <TableCell sx={{ maxWidth: 220, fontSize: "0.8rem" }}>
                    {r.nextMatrixCoRecipient}
                  </TableCell>
                  <TableCell align="right">{r.fillCount}</TableCell>
                  <TableCell align="right">{formatInr(r.total)}</TableCell>
                  <TableCell align="right">
                    {r.level === 15 ? "NO Upgrade" : formatInr(r.autoUpgrade)}
                  </TableCell>
                  <TableCell align="right">{formatInr(r.coPoints)}</TableCell>
                  <TableCell align="right">{formatInr(r.cumulativeCo)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search email or Program ID"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, opacity: 0.5 }} /> }}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Autocomplete
              size="small"
              options={filteredPrograms}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((p) => {
                  const label = `${p.programId} — ${p.userEmail || p.userId}`.toLowerCase();
                  return label.includes(q);
                });
              }}
              getOptionLabel={(p) =>
                `${p.programId} — ${p.userEmail || p.userId} (₹${p.packageAmount}, L${p.currentLevel})`
              }
              value={selectedProgram || null}
              onChange={(_, v) => setSelectedProgramId(v?.programId || null)}
              renderInput={(params) => (
                <TextField {...params} label="Select program to inspect" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {selectedProgram && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Program owner</Typography>
                <Typography variant="h6">{owner?.name || selectedProgram.userName}</Typography>
                <Typography variant="body2">{selectedProgram.userEmail}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong>ID:</strong> {selectedProgram.programId}
                </Typography>
                <Typography variant="body2">
                  <strong>Package:</strong> ₹{selectedProgram.packageAmount}
                </Typography>
                <Typography variant="body2">
                  <strong>Level:</strong> {selectedProgram.currentLevel} / 15
                </Typography>
                {selectedProgram.parentProgramId && (
                  <Typography variant="body2">
                    <strong>Parent AFP:</strong> {selectedProgram.parentProgramId}
                    {selectedProgram.spawnedFromWithdrawal ? " · withdrawal spawn" : ""}
                  </Typography>
                )}
                {selectedProgram.spawnedFromWithdrawalId && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Spawned from {selectedProgram.spawnedFromWithdrawalId}
                  </Typography>
                )}
                <Chip
                  size="small"
                  sx={{ mt: 1 }}
                  label={selectedProgram.status}
                  color={
                    selectedProgram.status === "active"
                      ? "success"
                      : selectedProgram.status === "on_hold"
                        ? "warning"
                        : "default"
                  }
                />
                {selectedProgram.holdReason && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Hold: {selectedProgram.holdReason}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Wallets (owner)</Typography>
                <Typography variant="body2">
                  CO Points available: ₹
                  {getAutoFillCoPointsWallet(owner).available.toLocaleString(
                    "en-IN"
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Matrix points: ₹
                  {(owner?.autoFillPoints?.available ?? 0).toLocaleString(
                    "en-IN"
                  )}
                  {(owner?.autoFillCoPoints?.available ?? 0) > 0
                    ? ` · Admin/genesis CO: ₹${(owner.autoFillCoPoints.available).toLocaleString("en-IN")}`
                    : ""}
                </Typography>
                <Typography variant="body2">
                  Upgrade reserved: ₹
                  {(owner?.autoFillUpgrade?.reserved ?? 0).toLocaleString("en-IN")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  L{selectedProgram.currentLevel} payment unit: ₹
                  {getLevelAmount(selectedProgram.currentLevel)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Matrix (this program)</Typography>
                <Typography variant="body2">
                  Positions: {positions.length} (
                  {positions.filter((p) => p.positionStatus === "filled").length} filled)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Every level: 1st payment → CO Points (withdrawal), 2nd → reserved, 3rd +
                  reserve → next level (L15: all three to wallet).
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        {selectedProgram ? "Money flow (selected program)" : "Recent money flows (all programs)"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Arrows show direction of value. &quot;From&quot; is usually the downline who triggered a matrix
        fill; &quot;To&quot; is who received Points or Upgrade balance.
      </Typography>

      {flowRows.length === 0 ? (
        <Alert severity="info">No ledger entries yet for this view.</Alert>
      ) : (
        flowRows.map(({ row, flow }) => (
          <FlowArrow
            key={row.id}
            from={flow.from}
            to={flow.to}
            amount={row.amount}
            why={flow.why}
            type={flow.type}
          />
        ))
      )}

      {selectedProgram?.programId === MAHESH_AUTO_FILL_PROGRAM_ID && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Level 2 — ₹300 Auto Fill payment flow (Mahesh)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Main account: mahesh1994.g@gmail.com ({MAHESH_AUTO_FILL_PROGRAM_ID}). Serial
            AFP-000014 / 000015 / 000016 unchanged — only allocation by payment #.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Payment</TableCell>
                  <TableCell>AFP serial</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Allocation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MAHESH_L2_SERIAL_PAYMENTS.map((r) => (
                  <TableRow key={r.paymentIndex}>
                    <TableCell>
                      {r.paymentIndex === 1 ? "1st" : r.paymentIndex === 2 ? "2nd" : "3rd"}
                    </TableCell>
                    <TableCell>{r.programId}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{r.accountEmail}</TableCell>
                    <TableCell>L{r.recipientLevel}</TableCell>
                    <TableCell align="right">{formatInr(300)}</TableCell>
                    <TableCell>
                      {r.allocation === "WITHDRAWAL" ? "Withdrawal" : "Next level upgrade"}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatInr(900)}</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {selectedProgram && positions.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Filled rows are ordered by <strong>payment time</strong> (1st → 3rd of the cycle).
            Mahesh L1: pay #1 genesis child AFP-000014, then AFP-000015 / 000016 for upgrade.
            Mahesh L2 (₹300 each): pay #1 → AFP-000014 withdrawal · #2 → AFP-000015 L1 upgrade ·
            #3 → AFP-000016 L2 upgrade.
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Pay #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Filler AFP / sender</TableCell>
                  <TableCell>Filler AFP level</TableCell>
                  <TableCell>Fill source</TableCell>
                  <TableCell align="right">Global fill #</TableCell>
                  <TableCell>Why it matters</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const filledChronoByLevel = {};
                  positions.forEach((p) => {
                    if (p.positionStatus !== "filled") return;
                    const L = p.level;
                    if (!filledChronoByLevel[L]) filledChronoByLevel[L] = [];
                    filledChronoByLevel[L].push(p);
                  });
                  Object.values(filledChronoByLevel).forEach((arr) => {
                    arr.sort(
                      (a, b) =>
                        new Date(a.filledAt || 0).getTime() -
                        new Date(b.filledAt || 0).getTime()
                    );
                  });
                  const payIndexByPosId = {};
                  Object.entries(filledChronoByLevel).forEach(([, arr]) => {
                    arr.forEach((p, i) => {
                      payIndexByPosId[p.id] = i + 1;
                    });
                  });

                  if (selectedProgram?.programId === MAHESH_AUTO_FILL_PROGRAM_ID) {
                    positions.forEach((p) => {
                      if (p.positionStatus !== "filled" || !p.filledByProgramId) return;
                      const serialPay = maheshPayIndexForFillerProgramId(
                        p.filledByProgramId
                      );
                      if (serialPay != null) {
                        payIndexByPosId[p.id] = serialPay;
                      }
                    });
                  }

                  const resolvePayNum = (pos) => {
                    if (pos.positionStatus !== "filled") return null;
                    return payIndexByPosId[pos.id] || pos.positionIndex;
                  };

                  const sortedPositions = [...positions].sort((a, b) => {
                    if (a.level !== b.level) return a.level - b.level;
                    const aPay = resolvePayNum(a);
                    const bPay = resolvePayNum(b);
                    if (aPay != null && bPay != null && aPay !== bPay) {
                      return aPay - bPay;
                    }
                    if (aPay != null && bPay == null) return -1;
                    if (aPay == null && bPay != null) return 1;
                    return (a.positionIndex || 0) - (b.positionIndex || 0);
                  });

                  return sortedPositions.map((pos) => {
                  const fillerEmail = pos.filledByUserId
                    ? userMap[pos.filledByUserId]?.email || pos.filledByUserId
                    : "—";
                  const fillerAfp = pos.filledByProgramId || "—";
                  const fillerProgram = programs.find((p) => p.programId === fillerAfp);
                  const filler =
                    fillerAfp !== "—"
                      ? `${fillerAfp}${fillerEmail !== "—" ? ` · ${fillerEmail}` : ""}`
                      : fillerEmail;
                  const amt = getLevelAmount(pos.level);
                  const payNum =
                    pos.positionStatus === "filled"
                      ? resolvePayNum(pos) ?? "—"
                      : "—";
                  let why = "Waiting for downline join / AFP creation";
                  if (pos.positionStatus === "filled") {
                    const idx = resolvePayNum(pos) || pos.positionIndex;
                    if (
                      selectedProgram?.programId === MAHESH_AUTO_FILL_PROGRAM_ID &&
                      Number(pos.level) === 2
                    ) {
                      const route = maheshL2SerialRouteForPaymentIndex(idx);
                      if (route) {
                        why =
                          route.allocation === "WITHDRAWAL"
                            ? `${idx === 1 ? "1st" : idx === 2 ? "2nd" : "3rd"} of 3 at L2 → ₹${amt} to ${route.programId} withdrawal (CO)`
                            : `${idx === 1 ? "1st" : idx === 2 ? "2nd" : "3rd"} of 3 at L2 → ₹${amt} to ${route.programId} L${route.recipientLevel} upgrade pool`;
                      }
                    } else if (pos.level === 15) {
                      why = `Fill at L15 → ₹${amt} CO Points (wallet)`;
                    } else if (idx === 1) {
                      why = `1st of 3 at L${pos.level} → ₹${amt} CO Points (withdrawal)`;
                    } else if (idx === 2) {
                      why = `2nd of 3 at L${pos.level} → ₹${amt} reserved in upgrade pool`;
                    } else {
                      why = `3rd of 3 at L${pos.level} → ₹${amt} + reserved ₹${amt} = ₹${amt * 2} → next level`;
                    }
                  }
                  return (
                    <TableRow key={pos.id}>
                      <TableCell>L{pos.level}</TableCell>
                      <TableCell>P{pos.positionIndex}</TableCell>
                      <TableCell>{payNum}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={pos.positionStatus}
                          color={pos.positionStatus === "filled" ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{filler}</TableCell>
                      <TableCell>
                        {fillerProgram
                          ? `L${fillerProgram.currentLevel || 1}`
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>
                        {pos.fillSource || "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.8rem" }}>
                        {pos.matrixFillSeq != null ? pos.matrixFillSeq : "—"}
                      </TableCell>
                      <TableCell>{why}</TableCell>
                    </TableRow>
                  );
                });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Paper sx={{ p: 2, bgcolor: primaryBlue[50] }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          How to read this
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              When someone you referred buys the ₹150 Auto Fill program and gets an AFP, they fill your next open
              matrix slot.
            </li>
            <li>
              At every level (payment = Auto Fill(L)): #1 → CO Points withdrawal, #2 → reserved in
              upgrade pool, #3 → same amount plus reserve unlocks the next level (e.g. ₹150+₹150=₹300,
              then at L2: ₹300+₹300=₹600). Level 15: all three payments to wallet.
            </li>
            <li>
              Level advances only after the 3rd matrix payment at that level (no extra gates on
              level-up; withdrawal rules are separate).
            </li>
            <li>
              Parent AFP only: approving a CO Points withdrawal at program level 2+ (from ₹300
              fills upward) creates the next global AFP serial; level 1 (₹150) withdrawals do not.
            </li>
          </ul>
        </Typography>
      </Paper>
    </Box>
  );
};

export default AutoFillAdminFlowDashboard;
